<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Popular Events</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Events with most bookings</p>
      </div>
      <span v-if="range" class="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-lg">
        {{ range }}
      </span>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !data" class="animate-pulse p-4 space-y-3">
      <div v-for="i in 4" :key="i" class="flex items-center gap-3">
        <div class="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
        <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-6"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No bookings yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Events with bookings will appear here.</p>
    </div>

    <!-- List -->
    <div v-else class="divide-y divide-gray-50 dark:divide-gray-800/60">
      <div class="flex items-center justify-between px-5 py-2.5 bg-gray-50 dark:bg-gray-800/40">
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Event</span>
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bookings</span>
      </div>
      <div v-for="event in stats" :key="event.event_name"
        class="flex items-center gap-3 px-5 py-3">
        <span class="w-3 h-3 rounded-full flex-shrink-0" :class="event.color"></span>
        <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex-1">{{ event.event_name }}</span>
        <span class="text-sm font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0">{{ event.bookings_count }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAnalyticsBookingsStore } from 'stores/analyticsBookings.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsBookingsStore()

const loading = computed(() => store.popularEventsLoading)
const data    = computed(() => store.popularEvents[props.duration] ?? null)
const range   = computed(() => data.value?.range ?? null)
const stats   = computed(() => (data.value?.stats ?? []).slice().sort((a, b) => b.bookings_count - a.bookings_count))
const isEmpty = computed(() => Array.isArray(data.value?.stats) && data.value.stats.length === 0)
</script>
